import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import * as VesselUtil from "@/utils/VesselUtil";
import * as VHUtil from "@/utils/VoyageHistoryUtil";
import C from "@/views/enterprise/addon/radar-grid/rg-object-history/Constants.js";
import * as turf from "@turf/turf";
import moment from "moment-timezone";
import isNumber from "lodash/isNumber";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import { VesselMixin } from "@/mixins";
import { createVectorFeatureWrapper } from "@/utils/MapVesselUtil";

export default {
  async getAisVesselsHistory(
    { commit },
    payload // {
    //   started_at, // string like '2024-02-14T06:24:34.684Z'
    //   ended_at, // string like '2024-02-14T06:24:34.684Z'
    //   north_east_latitude, // number
    //   north_east_longitude, // number
    //   south_west_latitude,  // number
    //   south_west_longitude, // number
    // }
  ) {
    const resMyVessels = await AiseaApiFactory.get("enterpriseVessel").index();

    const resAisVessels = await AiseaApiFactory.get(
      "enterpriseVoyageRecords"
    ).getVesselsAndPoint(payload);

    const resPlayBackObjList = [];
    for (const value of Object.values(resAisVessels.data)) {
      const vessel = VHUtil.vesselForRgHistory(value);

      const found = resMyVessels.data.vessels.find(
        el => el.id === vessel.vesselId
      );
      const isMyVessel = typeof found !== "undefined";
      resPlayBackObjList.push({
        ...vessel,
        heading: vessel.pointsArray[0].heading,
        startTime: vessel.pointsArray[0].epoch,
        endTime: vessel.pointsArray[vessel.pointsArray.length - 1].epoch,
        objectType: isMyVessel
          ? C.PLAYBACK_OBJECT_TYPE.MY_VESSELS
          : C.PLAYBACK_OBJECT_TYPE.OTHER_VESSELS,
      });
    }

    commit("SET_RG_PLAYBACK_OBJECTS", resPlayBackObjList);
  },

  async generateCurrentGeoJson({ state, getters }) {
    if (!Array.isArray(state.rgPlaybackObjects)) {
      return {
        points: turf.featureCollection([]),
        tracks: turf.featureCollection([]),
        vectors: turf.featureCollection([]),
      };
    }

    const { rgPlaybackObjects, playbackSettings } = state;
    const currentPlaybackData = rgPlaybackObjects
      .filter(({ startTime }) => {
        if (!isNumber(startTime)) return false;
        return startTime <= playbackSettings.now; // 開始時刻が現在時刻より後のものは表示しない
      })
      .filter(({ endTime }) => {
        if (!isNumber(endTime)) return false;
        const dateFrom = moment.unix(playbackSettings.now);
        const dateTo = moment.unix(endTime);
        const diff = dateFrom.diff(dateTo, "minutes");
        return diff < 1; // 1分経過したものは表示しない
      })
      .map(rgPlayBackObj => {
        const { pointsArray } = rgPlayBackObj;
        const idx = calcClosestPrevPointIndex(
          pointsArray,
          playbackSettings.now
        );
        const prevPoint = pointsArray[idx];
        const nextPoint = pointsArray[idx + 1];

        const interpolateRate = calcInterpolateRate({
          prevPoint,
          currentTimeStamp: playbackSettings.now,
          nextPoint,
        });
        const currentPoint = calcCurrentPoint(
          prevPoint,
          nextPoint,
          interpolateRate
        );
        return { rgPlayBackObj, idx, currentPoint };
      });

    // 戻りをまとめる
    const pointsPromisesFactory = async () => {
      return currentPlaybackData.map(({ rgPlayBackObj, currentPoint }) => {
        return createPointsFeatures(rgPlayBackObj, currentPoint);
      });
    };
    const tracksPromisesFactory = async () => {
      return currentPlaybackData.map(({ rgPlayBackObj, idx, currentPoint }) => {
        const { objectType } = rgPlayBackObj;

        return createTrackFeatures(
          rgPlayBackObj,
          currentPoint,
          idx,
          getters.isVisibleTrack(
            objectType,
            getRgPlaybackObjectId(rgPlayBackObj)
          )
        );
      });
    };
    const vectorsPromisesFactory = async () => {
      return currentPlaybackData.map(({ rgPlayBackObj, currentPoint }) => {
        return createVectorFeatureWrapper(
          rgPlayBackObj.vesselId,
          rgPlayBackObj,
          currentPoint,
          6
        );
      });
    };

    return await Promise.allSettled([
      pointsPromisesFactory(),
      tracksPromisesFactory(),
      vectorsPromisesFactory(),
    ]).then(([points, tracks, vectors]) => {
      return {
        points: turf.featureCollection(
          getFulfilledValueOrDefault(points, []).flat()
        ),
        tracks: turf.featureCollection(
          getFulfilledValueOrDefault(tracks, []).flat()
        ),
        vectors: turf.featureCollection(
          getFulfilledValueOrDefault(vectors, [])
        ),
      };
    });
  },

  async setPlaybackRequestSettings(
    { commit },
    payload = {
      // north_east_latitude: string,
      // north_east_longitude: string,
      // south_west_latitude: string,
      // south_west_longitude: string,
      // started_at: string,
      // ended_at: string,
      // zoom: string,
    }
  ) {
    commit("SET_PLAYBACK_REQUEST_SETTINGS", payload);
  },

  // 期間入力ダイアログを表示
  async activateDateInputDialog({ commit }) {
    commit("SET_RG_PLAYBACK_STATUS", C.PLAYBACK_STATUS.PAUSE);
    commit("SET_IS_ACTIVE_DATE_INPUT_DIALOG", true);
  },

  async resetVisibleTracks({ commit }) {
    commit("RESET_VISIBLE_TRACKS");
  },

  async resetState({ commit }) {
    let defaultState = {
      isInitial: true,
      playbackSettings: {},
      isActiveDateInputDialog: false,
      rgPlaybackObjects: {},
      layerFilters: {},
      visibleTracks: {
        vessels: [],
      },
      playbackRequestSettings: {},
    };
    commit("RESET_STATE", defaultState);
  },
};

/**
 * 指定されたタイムスタンプに最も近い要素を二分探索で検索
 * O(log n)
 *
 * @param {Array} pointsArray - ポイントの配列 (rgPlaybackObjects.pointsArray)
 * @param {number} timestamp - タイムスタンプ (playbackSettings.now)
 * @returns {number} - 最も近い要素のインデックス
 */
const binarySearchClosest = (
  pointsArray, // :Array (rgPlaybackObjects.pointsArray)
  timestamp // :number playbackSettings.now
) => {
  let left = 0;
  let right = pointsArray.length - 1;
  let closest = -1;

  while (left <= right) {
    const midIndex = Math.floor((left + right) / 2); // 中間のindex
    const midObject = pointsArray[midIndex];
    const midTimestamp = getPointTimestamp(midObject);

    if (timestamp < midTimestamp) {
      right = midIndex - 1;
    } else if (timestamp > midTimestamp) {
      left = midIndex + 1;
    } else {
      return midIndex;
    }
    closest = midIndex;
  }
  return closest;
};

/**
 * 指定された時間に最も近いポイントのインデックスを取得
 * 未通過のポイントが最も近い場合は、その前のポイントのインデックスを返す
 * 最も近いポイントがなかった場合は、-1を返す
 * O(log n)
 *
 * @param {Array} pointsArray - ポイントの配列 (rgPlaybackObjects.pointsArray)
 * @param {number} timeToDisplay - 表示する時間 (playbackSettings.now)
 * @returns {number} - 最も近いポイントのインデックス
 */
const calcClosestPrevPointIndex = (pointsArray, timeToDisplay) => {
  const closestIndex = binarySearchClosest(pointsArray, timeToDisplay); // O(log n)

  if (closestIndex === -1) return closestIndex;
  if (timeToDisplay < getPointTimestamp(pointsArray[closestIndex])) {
    return closestIndex - 1;
  }
  return closestIndex;
};

/**
 * rgPlayBackObj、interpolatedCurrentPoint、currentIndexを使用してトラックのフィーチャーを作成
 *
 * @param {Object} rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 線形補間された現在のポイント
 * @param {number} currentIndex - 現在のインデックス
 * @param {boolean} [isVisibleTrack=false] - トラックの可視性（省略可）
 * @returns {Object} トラックのフィーチャー(Array<turf.lineString>)
 */
const createTrackFeatures = (
  rgPlayBackObj,
  interpolatedCurrentPoint,
  currentIndex,
  isVisibleTrack = false // boolean
) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];
  if (!isNumber(currentIndex)) return [];

  const { lon, lat } = interpolatedCurrentPoint;
  const coordinates = [lon, lat];
  const trackFeaturesCreator = VHUtil.createTrackFeatures;
  const trackFeature = trackFeaturesCreator(
    rgPlayBackObj.objectType,
    getRgPlaybackObjectId(rgPlayBackObj),
    isVisibleTrack,
    rgPlayBackObj.pointsArray,
    currentIndex + 1,
    coordinates,
    rgPlayBackObj
  );
  return trackFeature;
};

/**
 * ポイントのフィーチャーの配列を作成
 * ポイントのフィーチャーには、ポイントとポリゴンが含まれます
 *
 * @param {Object} rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 補間された現在のポイント
 * @returns {Array} ポイントのフィーチャーの配列(Array<turf.feature>)
 */
const createPointsFeatures = (rgPlayBackObj, interpolatedCurrentPoint) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];

  const { MY_VESSELS, OTHER_VESSELS, RG_OBJECT } = C.PLAYBACK_OBJECT_TYPE;
  const { objectType } = rgPlayBackObj;
  if (![MY_VESSELS, OTHER_VESSELS, RG_OBJECT].includes(objectType)) return [];

  if (objectType === MY_VESSELS || objectType === OTHER_VESSELS) {
    return [
      createVesselPointFeature(rgPlayBackObj, interpolatedCurrentPoint),
      ...createVesselPolygonFeatures(rgPlayBackObj, interpolatedCurrentPoint),
    ];
  }
  return [];
};

const VESSEL_CATEGORY_IDS = new Set(VesselMixin.methods.getVesselCategoryIds()); // : Set<string> (set of number of string)
const vesselCategoryName = (
  categoryId // number of string
) => {
  const FALLBACK_NAME = "---";
  if (!VESSEL_CATEGORY_IDS.has(categoryId)) return FALLBACK_NAME;
  const vesselCategoryProperty = VesselMixin.methods.getVesselCategoryProps(
    Number(categoryId)
  );
  return vesselCategoryProperty.name_ja;
};

const roundToDecimalPlace = (
  speed, // number
  decimalPlace = 1 // number
) => {
  return Math.round(speed * 10 ** decimalPlace) / 10 ** decimalPlace; // number
};

/**
 * rgPlayBackObjと補間された現在のポイントを使用して、船舶のポイントフィーチャを作成
 *
 * @param {Object} rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 補間された現在のポイント
 * @returns {Object} - 船舶のポイントフィーチャ(turf.feature)
 */
const createVesselPointFeature = (rgPlayBackObj, interpolatedCurrentPoint) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];

  const { MY_VESSELS, OTHER_VESSELS } = C.PLAYBACK_OBJECT_TYPE;
  const { objectType } = rgPlayBackObj;
  if (![MY_VESSELS, OTHER_VESSELS].includes(objectType)) return [];
  const {
    vesselId,
    vesselName,
    // epoch, // 現状はundefinedになってしまう?
    hasSize,
    vesselType, // cat
    size,
  } = rgPlayBackObj;

  const { lon, lat, heading, speed, epoch } = interpolatedCurrentPoint;
  // epoch: 1706259459;
  // heading: 328;
  // lat: 30.735756513636364;
  // lon: 130.88593727272726;
  // speed: 0;
  // timeDiff: 0;
  const {
    l, // 全長
    w, // 全幅
  } = size || {};
  const props = {
    vesselId: vesselId,
    vesselName: vesselName,
    objectType: objectType,
    epoch: epoch,
    hasSize: hasSize,
    icon: VesselUtil.getSymbolIconNameLightWeight(Number(vesselType), 1.0, 0),
    speed: roundToDecimalPlace(speed, 1),
    headingText: roundToDecimalPlace(heading, 1),
    category: vesselCategoryName(vesselType),
    length: l,
    width: w,
    heading: heading || 0,
    marker: "vessel-owned-marker-25",
    // エリア外のfilter用
    lon,
    lat,
  };

  return turf.feature(
    {
      type: "Point",
      coordinates: [lon, lat],
    },
    props
  );
};

/**
 * rgPlayBackObjと補間された現在のポイントを受け取り、
 * 船舶のポリゴンフィーチャーを作成
 *
 * @param {Object} rgPlayBackObj - rgPlayBackObj
 * @param {Object} interpolatedCurrentPoint - 補間された現在のポイント
 * @returns {Array} 船舶のポリゴンフィーチャーの配列
 */
const createVesselPolygonFeatures = (
  rgPlayBackObj,
  interpolatedCurrentPoint
) => {
  if (!isObject(rgPlayBackObj)) return [];
  if (!isObject(interpolatedCurrentPoint)) return [];
  if (!rgPlayBackObj.hasSize) return [];

  const { MY_VESSELS, OTHER_VESSELS } = C.PLAYBACK_OBJECT_TYPE;
  const { objectType } = rgPlayBackObj;
  if (![MY_VESSELS, OTHER_VESSELS].includes(objectType)) return [];

  const { lon, lat, heading } = interpolatedCurrentPoint;
  return VHUtil.createPolygonFeature(
    rgPlayBackObj.vesselId,
    {
      lon,
      lat,
      h: heading,
    },
    rgPlayBackObj.polygonData
  ).map(element => {
    element.properties["vesselId"] = rgPlayBackObj.vesselId;
    element.properties["hasSize"] = rgPlayBackObj.hasSize;
    element.properties["objectType"] = objectType;
    // エリア外のfilter用
    element.properties["lat"] = lat;
    element.properties["lon"] = lon;
    return element;
  });
};

/**
 * 現在のタイムスタンプに基づいて、2つのポイント間の補間率を計算。
 * @param {Object} options - オプションオブジェクト。
 * @param {Object} options.prevPoint - 前のポイントオブジェクト。
 * @param {Object} options.nextPoint - 次のポイントオブジェクト。
 * @param {number} options.currentTimeStamp - 現在のタイムスタンプ。
 * @returns {number} 補間率。 0 ~ 1
 */
const calcInterpolateRate = ({
  prevPoint: pP, //Object
  nextPoint: nP, // Object
  currentTimeStamp: ct, // number
}) => {
  if (!isObject(pP)) return 0;
  if (!isObject(nP)) return 0;
  if (!isNumber(ct)) return 0;

  const _calcInterpolateRate = ({
    prevPointTimestamp: pPT,
    currentTimeStamp: ct,
    nextPointTimestamp: nPT,
  }) => {
    if (!isNumber(pPT) || !isNumber(ct) || !isNumber(nPT)) return 0;

    if (nPT === pPT) return 0; // 0除算回避
    return (ct - pPT) / (nPT - pPT);
  };

  return _calcInterpolateRate({
    prevPointTimestamp: getPointTimestamp(pP),
    currentTimeStamp: ct,
    nextPointTimestamp: getPointTimestamp(nP),
  });
};

/**
 * ポイントのタイムスタンプを取得
 * @param {Object} point - ポイントオブジェクト
 * @param {number} point.epoch - エポックタイムスタンプ
 * @param {number} point.t - タイムスタンプ
 * @returns {number} - タイムスタンプ
 */
const getPointTimestamp = point => {
  if (!isObject(point)) return 0;
  const { epoch, t } = point;
  if (!isNumber(epoch) && !isNumber(t)) return 0;

  return epoch || t;
};

/**
 * rgPlayBackObjのIDを取得
 * @param {Object} rgPlayBackObj
 * @returns {string} - Radar Grid再生オブジェクトのID
 * @throws {Error} - IDが無効な場合にエラーがスローされます
 */
const getRgPlaybackObjectId = rgPlayBackObj => {
  if (!hasRgPlaybackObjectValidId(rgPlayBackObj)) {
    throw new Error("Error Invalid Id");
  }
  const { vesselId, objectId } = rgPlayBackObj;
  return vesselId || objectId;
};

/**
 * rgPlayBackObjが有効なIDを持っているかどうかをチェック
 * @param {Object} rgPlayBackObj - チェックするrgPlayBackObj
 * @returns {boolean} - rgPlayBackObjが有効なIDを持っている場合はtrue、それ以外の場合はfalseを返す
 * idはvesselIdまたはobjectIdのいずれかです
 */
const hasRgPlaybackObjectValidId = rgPlayBackObj => {
  if (!isObject(rgPlayBackObj)) return false;
  const { vesselId, objectId } = rgPlayBackObj;
  if (isString(vesselId) && vesselId.length > 0) return true;
  return isString(objectId) && objectId.length > 0;
};

/**
 * 補間率を考慮して、前のポイントと次のポイントから現在のポイントを計算
 *
 * @param {Object} prevPoint - 前のポイント
 * @param {number} interpolateRate - 補間率
 * @param {Object} nextPoint - 次のポイント
 * @returns {Object} 現在のポイント - 戻りの構造はprevPointと同じ
 * @throws {Error} 引数が無効な場合にエラーがスローされます
 */
const calcCurrentPoint = (prevPoint, nextPoint, interpolateRate = 0) => {
  if (!isObject(prevPoint)) throw new Error("Error Invalid Augments");
  if (!(isObject(nextPoint) || isUndefined(nextPoint))) {
    throw new Error("Error Invalid Augments");
  }
  if (!isNumber(interpolateRate)) throw new Error("Error Invalid Augments");
  if (!interpolateRate < 0 || 1 < interpolateRate) {
    throw new Error("Error Invalid Augments");
  }

  if (isUndefined(nextPoint)) return prevPoint;
  if (interpolateRate === 0) return prevPoint;

  const _calcInterpolateRate = (prevPoint, interpolateRate, nextPoint) => {
    return {
      ...prevPoint, // NOTE: lon, lat以外はprevPointの値を使用している
      lon: prevPoint.lon + (nextPoint.lon - prevPoint.lon) * interpolateRate,
      lat: prevPoint.lat + (nextPoint.lat - prevPoint.lat) * interpolateRate,
    };
  };
  return _calcInterpolateRate(prevPoint, interpolateRate, nextPoint);
};

/**
 * 指定された settledResult の値が "fulfilled" の場合はその値を返し、
 * そうでない場合はデフォルト値を返す
 *
 * @param {Object} settledResult - 処理結果のオブジェクト(Promise.allSettledの戻り値の要素)
 * @param {string} settledResult.status - 処理の状態 ("fulfilled" or "rejected")
 * @param {*} settledResult.value - 処理結果の値
 * @param {*} [defaultValue=null] - デフォルト値 (省略可能)
 * @returns {*} - 処理結果の値またはデフォルト値
 */
const getFulfilledValueOrDefault = (
  settledResult, // { status: "fulfilled", value: any } | { status: "rejected", reason: any }
  defaultValue = null // any
) => {
  return settledResult.status === "fulfilled"
    ? settledResult.value
    : defaultValue;
};
